import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, PrepTaskDto, PREP_AHEAD_KINDS } from "../api/client";
import {
  disablePush,
  enablePush,
  getPushDiagnostics,
  getPushState,
  isPushSupported,
  PushState,
} from "../push";

/** Data d'avui a Europe/Madrid com a string YYYY-MM-DD. */
function todayMadrid(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

const WEEKDAYS = ["diumenge", "dilluns", "dimarts", "dimecres", "dijous", "divendres", "dissabte"];

function relativeDay(dateStr: string, today: string): string {
  const target = new Date(dateStr + "T00:00:00Z");
  const base = new Date(today + "T00:00:00Z");
  const delta = Math.round((target.getTime() - base.getTime()) / 86_400_000);
  if (delta < 0) return "endarrerit";
  if (delta === 0) return "avui";
  if (delta === 1) return "demà";
  if (delta === 2) return "demà passat";
  return WEEKDAYS[target.getUTCDay()];
}

function kindMeta(kind: string) {
  return PREP_AHEAD_KINDS.find((k) => k.value === kind);
}

export function Prep() {
  const [tasks, setTasks] = useState<PrepTaskDto[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pushState, setPushState] = useState<PushState>("unsupported");
  const [pushBusy, setPushBusy] = useState(false);
  const [diag, setDiag] = useState<string | null>(null);
  const today = todayMadrid();

  async function load() {
    const list = await api.get<PrepTaskDto[]>("/prep-tasks");
    setTasks(list);
  }

  useEffect(() => {
    load();
    if (isPushSupported()) getPushState().then(setPushState);
  }, []);

  async function toggleDone(t: PrepTaskDto) {
    setBusyId(t.id);
    try {
      const action = t.status === "done" ? "undo" : "done";
      const updated = await api.post<PrepTaskDto>(`/prep-tasks/${t.id}/${action}`);
      setTasks((cur) => (cur ? cur.map((x) => (x.id === t.id ? updated : x)) : cur));
      window.dispatchEvent(new Event("naramenu:prep-changed"));
    } finally {
      setBusyId(null);
    }
  }

  async function togglePush() {
    setPushBusy(true);
    try {
      setPushState(pushState === "subscribed" ? await disablePush() : await enablePush());
    } catch (e: any) {
      alert(e?.message ?? "No s'ha pogut activar les notificacions.");
    } finally {
      setPushBusy(false);
    }
  }

  const { todo, done, upcoming } = useMemo(() => {
    const all = tasks ?? [];
    return {
      todo: all.filter((t) => t.status === "pending" && t.dueDate <= today),
      upcoming: all.filter((t) => t.status === "pending" && t.dueDate > today),
      done: all.filter((t) => t.status === "done"),
    };
  }, [tasks, today]);

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <h1 style={{ fontSize: 26, marginBottom: 6 }}>Preparacions</h1>
      <p className="text-muted" style={{ fontSize: 14, marginBottom: 18 }}>
        Coses a punt el dia abans (remull, descongelar, nevera…). Les compartim tots:
        el primer que en marca una la treu de la llista de la resta.
      </p>

      {pushState !== "subscribed" && isPushSupported() && (
        <div className="card" style={{ padding: 14, marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 22 }}>🔔</span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <b style={{ fontSize: 14 }}>Rep avisos al mòbil</b>
            <p className="text-muted" style={{ fontSize: 12.5 }}>
              {pushState === "denied"
                ? "El navegador diu que estan bloquejats. Prova d'activar-los igualment; si no surt cap diàleg, cal reiniciar el permís des dels ajustos del dispositiu."
                : "Activa les notificacions per rebre'ls encara amb l'app tancada."}
            </p>
          </div>
          <button type="button" className="btn btn-primary" disabled={pushBusy} onClick={togglePush}>
            {pushBusy ? "…" : "Activa"}
          </button>
        </div>
      )}

      {tasks === null ? (
        <p className="text-muted">Carregant…</p>
      ) : todo.length === 0 && upcoming.length === 0 && done.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: "center" }}>
          <div style={{ fontSize: 34 }}>🍃</div>
          <p style={{ marginTop: 8, fontWeight: 600 }}>Res a preparar per avançat</p>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Marca ingredients que necessitin remull, descongelar o nevera a les receptes.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <PrepGroup title="Per fer" tasks={todo} today={today} busyId={busyId} onToggle={toggleDone} highlight />
          <PrepGroup title="Més endavant" tasks={upcoming} today={today} busyId={busyId} onToggle={toggleDone} />
          <PrepGroup title="Fetes" tasks={done} today={today} busyId={busyId} onToggle={toggleDone} muted />
        </div>
      )}
    </div>
  );
}

function PrepGroup({
  title, tasks, today, busyId, onToggle, highlight, muted,
}: {
  title: string;
  tasks: PrepTaskDto[];
  today: string;
  busyId: number | null;
  onToggle: (t: PrepTaskDto) => void;
  highlight?: boolean;
  muted?: boolean;
}) {
  if (tasks.length === 0) return null;
  return (
    <section>
      <h2 style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--muted)", marginBottom: 10 }}>
        {title} · {tasks.length}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {tasks.map((t) => {
          const meta = kindMeta(t.prepAhead);
          const isDone = t.status === "done";
          const overdue = !isDone && t.dueDate < today;
          return (
            <div
              key={t.id}
              className="card"
              style={{
                padding: "12px 14px",
                display: "flex",
                gap: 12,
                alignItems: "center",
                opacity: muted ? 0.7 : 1,
                borderColor: overdue ? "var(--danger)" : highlight ? "var(--accent)" : undefined,
              }}
            >
              <span style={{ fontSize: 24, filter: isDone ? "grayscale(1)" : "none" }} aria-hidden>
                {meta?.emoji ?? "⏲️"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, textDecoration: isDone ? "line-through" : "none" }}>
                  {t.kindLabel}: {t.ingredient}
                </div>
                <div className="text-muted" style={{ fontSize: 12.5, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span>per <b>{relativeDay(t.mealDate, today)}</b></span>
                  <span>·</span>
                  <Link to={`/receptes/${t.recipeId}`} style={{ color: "inherit", textDecoration: "underline" }}>
                    {t.recipeName}
                  </Link>
                  {t.prepNote && <span>· {t.prepNote}</span>}
                </div>
                {isDone && t.doneBy && (
                  <div style={{ fontSize: 12, marginTop: 3, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span
                      className="av"
                      style={{ background: t.doneBy.color, width: 18, height: 18, fontSize: 10 }}
                      aria-hidden
                    >
                      {t.doneBy.emoji}
                    </span>
                    <span className="text-muted">ho ha fet {t.doneBy.name}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className={isDone ? "btn btn-ghost" : "btn btn-primary"}
                disabled={busyId === t.id}
                onClick={() => onToggle(t)}
                style={{ flexShrink: 0 }}
              >
                {busyId === t.id ? "…" : isDone ? "Desfés" : "Fet"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
